import React, { useState } from "react";
import type { CompanyLocation } from "@mysnowscape/snow-lib";

import CompanyLocationForm, {
  type CompanyLocationFormValues,
} from "../../../../../../components/CompanyLocations/CompanyLocationForm";
import type { RouteEndpointLocation } from "../../../../../../types/routeBuilder";
import LocationSelect from "./LocationSelect";

type LocationPickerProps = {
  label?: string;
  locations: CompanyLocation[];
  selected: RouteEndpointLocation | null | undefined;
  saving?: boolean;
  onAddingAddressChange?: (adding: boolean) => void;
  onSelect: (location: CompanyLocation) => void;
  onSaveNew: (
    values: CompanyLocationFormValues,
  ) => Promise<CompanyLocation | null> | CompanyLocation | null;
};

/** Choose a saved company location or add an address inline. */
export default function LocationPicker({
  label,
  locations,
  selected,
  saving = false,
  onAddingAddressChange,
  onSelect,
  onSaveNew,
}: LocationPickerProps) {
  // Track explicit address entry separately; an empty list may still be loading.
  const [showNewAddress, setShowNewAddress] = useState(false);
  const addingAddress = showNewAddress || locations.length === 0;
  const changeAddingAddress = (adding: boolean) => {
    setShowNewAddress(adding);
    onAddingAddressChange?.(adding);
  };

  return (
    <div className="min-w-0" data-testid="location-picker">
      {label ? (
        <p className="mb-2 text-sm font-medium text-slate-700">{label}</p>
      ) : null}
      {!addingAddress ? (
        <LocationSelect
          label={label ?? "Location"}
          locations={locations}
          selected={selected}
          disabled={saving}
          onSelect={onSelect}
          onNewAddress={() => changeAddingAddress(true)}
        />
      ) : null}

      {addingAddress ? (
        <CompanyLocationForm
          mode="create"
          compact
          saveLabel="Save"
          saving={saving}
          onSave={async (values) => {
            const saved = await onSaveNew(values);
            if (saved) changeAddingAddress(false);
          }}
          onCancel={
            locations.length > 0 ? () => changeAddingAddress(false) : undefined
          }
        />
      ) : null}
    </div>
  );
}
